import React, { useState } from 'react';
import {
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Clock,
  History,
  Loader2
} from 'lucide-react';
import { api } from '../services/api';

const formatMontant = (montant) => (
  `${new Intl.NumberFormat('fr-FR').format(Math.round(Number(montant || 0)))} FCFA`
);

// Les valeurs viennent de MySQL en UTC et sont relues comme telles par le pool
// applicatif : les rendre dans le fuseau du navigateur donne l'heure réelle.
const formatHorodatage = (valeur) => {
  if (!valeur) return '—';
  const date = new Date(valeur);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

const STATUTS = {
  en_attente: { libelle: 'En attente', classe: 'border-amber-200 bg-amber-50 text-amber-800' },
  approuvee: { libelle: 'Validée', classe: 'border-emerald-200 bg-emerald-50 text-emerald-800' },
  refusee: { libelle: 'Refusée', classe: 'border-red-200 bg-red-50 text-red-700' },
  // ⚠️ CE STATUT DOIT FIGURER ICI, SINON IL S'AFFICHE « En attente ».
  // Le rendu fait `STATUTS[statut] || STATUTS.en_attente` : un statut inconnu
  // retombe sur « en attente », et une demande fermée se lirait comme une demande
  // qui court encore — exactement le contraire de la vérité.
  //
  // `annulee` est posé par le Centre de Support sur les demandes JUMELLES : quand
  // un même geste a été demandé plusieurs fois, décider de l'une ferme les autres.
  // Ce ne sont ni des approbations ni des refus, et les présenter comme tels
  // raconterait une décision qui n'a pas été prise.
  annulee: { libelle: 'Annulée', classe: 'border-gray-200 bg-gray-50 text-gray-600' }
};

const TYPES = {
  modification_reduction_eleve: 'border-[#cfe3ec] bg-[#eef7fb] text-[#246990]',
  radiation_eleve: 'border-red-200 bg-red-50 text-red-700',
  restauration_eleve: 'border-emerald-200 bg-emerald-50 text-emerald-800',
  // Les mouvements de famille : même famille de couleur, pour qu'ils se lisent
  // comme un groupe distinct des gestes financiers de l'élève seul.
  ajout_eleve_famille_nombreuse: 'border-indigo-200 bg-indigo-50 text-indigo-700',
  retrait_eleve_famille_nombreuse: 'border-indigo-200 bg-indigo-50 text-indigo-700',
  creation_famille_depuis_eleves_seuls: 'border-indigo-200 bg-indigo-50 text-indigo-700',
  modification_frais_inscription: 'border-purple-200 bg-purple-50 text-purple-700',
  changement_classe_eleve: 'border-sky-200 bg-sky-50 text-sky-700'
};

/**
 * Répond à une seule question, posée là où elle se pose : pourquoi cet élève
 * paie-t-il ce montant plutôt que le tarif de sa classe ?
 *
 * Le contenu est chargé au premier dépliage, pas à l'ouverture du dossier : la
 * plupart des consultations ne s'y intéressent pas.
 */
const JournalModificationsEleve = ({ eleveId }) => {
  const [ouvert, setOuvert] = useState(false);
  const [chargement, setChargement] = useState(false);
  const [chargeUneFois, setChargeUneFois] = useState(false);
  const [erreur, setErreur] = useState('');
  const [journal, setJournal] = useState({ evenements: [], total: 0, en_attente: 0 });

  const charger = async () => {
    setChargement(true);
    setErreur('');
    try {
      const result = await api.eleves.getJournalModifications(eleveId);
      if (result.success) {
        setJournal(result.data);
      } else {
        setErreur(result.message || "Impossible de lire l'historique.");
      }
    } catch (error) {
      setErreur("Erreur lors du chargement de l'historique.");
    } finally {
      setChargement(false);
      setChargeUneFois(true);
    }
  };

  const basculer = () => {
    const prochainEtat = !ouvert;
    setOuvert(prochainEtat);
    if (prochainEtat && !chargeUneFois) charger();
  };

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <button
        type="button"
        onClick={basculer}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors rounded-lg"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <History className="w-4 h-4 text-[#246990]" />
          Historique
          {chargeUneFois && (
            <span className="text-xs font-normal text-gray-500">({journal.total})</span>
          )}
        </span>
        <span className="flex items-center gap-2">
          {chargeUneFois && journal.en_attente > 0 && (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800">
              <Clock className="w-3 h-3" />
              {journal.en_attente} en attente
            </span>
          )}
          {ouvert ? (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400" />
          )}
        </span>
      </button>

      {ouvert && (
        <div className="border-t border-gray-200 px-4 py-3">
          {chargement ? (
            <div className="py-6 flex items-center justify-center text-[#246990] text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Lecture de l'historique…
            </div>
          ) : erreur ? (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{erreur}</span>
            </div>
          ) : journal.evenements.length === 0 ? (
            <p className="py-4 text-sm text-gray-500 text-center">
              Aucune modification enregistrée.
            </p>
          ) : (
            <div className="space-y-2">
              {journal.evenements.map((evenement) => {
                const statut = STATUTS[evenement.statut] || STATUTS.en_attente;
                const montantsConnus = evenement.montant_avant !== null && evenement.montant_apres !== null;

                return (
                  <div key={evenement.id} className="rounded-md border border-gray-200 bg-gray-50 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${TYPES[evenement.type] || 'border-gray-200 bg-white text-gray-700'}`}>
                          {evenement.type_libelle}
                        </span>
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${statut.classe}`}>
                          {statut.libelle}
                        </span>
                        {montantsConnus && (
                          <span className="text-sm font-semibold text-gray-900">
                            {formatMontant(evenement.montant_avant)} → {formatMontant(evenement.montant_apres)} / mois
                          </span>
                        )}
                      </div>
                      <span className="text-[11px] text-gray-500">
                        Demandé le {formatHorodatage(evenement.date_demande)}
                        {evenement.date_decision && ` · Traité le ${formatHorodatage(evenement.date_decision)}`}
                      </span>
                    </div>

                    {!montantsConnus && evenement.description && (
                      <p className="mt-2 text-xs text-gray-600">{evenement.description}</p>
                    )}

                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-gray-600">
                      <div>
                        <span className="text-gray-500">Demandé par :</span>{' '}
                        {evenement.demandeur || 'Utilisateur inconnu'}
                      </div>
                      <div>
                        <span className="text-gray-500">Validé par :</span>{' '}
                        {evenement.validateur || (evenement.statut === 'en_attente' ? 'en attente' : '—')}
                      </div>
                      {evenement.famille && (
                        <div className="sm:col-span-2">
                          <span className="text-gray-500">Famille :</span> {evenement.famille}
                        </div>
                      )}
                      {evenement.mois_effet && (
                        <div className="sm:col-span-2">
                          <span className="text-gray-500">Effet à partir de :</span> {evenement.mois_effet}
                        </div>
                      )}
                    </div>

                    {evenement.motif && (
                      <p className="mt-2 text-xs text-gray-700">
                        <span className="text-gray-500">Motif :</span> {evenement.motif}
                      </p>
                    )}

                    {evenement.motifs_detail?.length > 0 && (
                      <ul className="mt-1 space-y-0.5 text-[11px] text-gray-600">
                        {evenement.motifs_detail.map((detail, index) => (
                          <li key={index}>· {detail}</li>
                        ))}
                      </ul>
                    )}

                    {evenement.commentaire_decision && (
                      <p className="mt-2 text-xs text-gray-700">
                        <span className="text-gray-500">Décision :</span> {evenement.commentaire_decision}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JournalModificationsEleve;
